
import React, { useState, useEffect } from 'react';
import { ChevronDown, RefreshCw, Bot } from 'lucide-react';
import Overview from './Overview';
import Documents from './Documents';
import CreditMemo from './CreditMemo';
import DealChatbot from './DealChatbot';
import dealsApi, { DealHeader } from '../../api/dealsApi';
import { useBusiness } from '../../context/BusinessContext';
import { Header } from '../../features/auth/components/Header';

const TABS = ['Overview', 'Documents', 'Credit Memo'];

const DealsLayout: React.FC = () => {
    const [activeTab, setActiveTab] = useState('Overview');
    const [header, setHeader] = useState<DealHeader | null>(null);
    const [isStatusOpen, setIsStatusOpen] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isChatMaximized, setIsChatMaximized] = useState(false);

    const { selected } = useBusiness();
    const businessSlug = selected?.slug;

    useEffect(() => {
        if (!businessSlug) return;
        dealsApi.getHeader(businessSlug).then(setHeader).catch(console.error);
    }, [businessSlug]);

    const handleStatusChange = async (newStatus: string) => {
        if (!businessSlug) return;
        try {
            await dealsApi.updateStatus(businessSlug, newStatus);
            setHeader(prev => prev ? { ...prev, status: newStatus, updated_at: 'Just now' } : null);
            setIsStatusOpen(false);
        } catch (error) {
            console.error("Failed to update status", error);
        }
    };

    return (
        <div className="flex flex-1 overflow-hidden bg-transparent">
            {/* Middle Content Area: Scrollable (Vertical + Horizontal) */}
            <div className="flex-1 overflow-auto scrollbar-hide">
                <div className="max-w-[1440px] mx-auto w-full min-w-[1200px] flex flex-col">
                    {/* Top Bar - Now aligned with content grid */}
                    <Header />

                    {/* Deal Header Section */}
                    <div className="px-8 pb-6 pt-0">
                        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-4">
                                    <h1 className="text-2xl font-bold text-gray-900 capitalize">{header?.business_name || businessSlug?.replace(/_/g, ' ') || 'Loading...'}</h1>
                                    <div className="relative">
                                        <button
                                            onClick={() => setIsStatusOpen(!isStatusOpen)}
                                            className={`flex items-center gap-2 px-3 py-1.5 bg-white border rounded text-sm font-medium hover:bg-gray-50 transition-colors ${header?.status === 'Approved' ? 'text-green-700 border-green-200 bg-green-50' :
                                                header?.status === 'Rejected' ? 'text-red-700 border-red-200 bg-red-50' :
                                                    'text-gray-600 border-gray-300'
                                                }`}
                                        >
                                            {header?.status || 'In Review'}
                                            <ChevronDown className="w-4 h-4" />
                                        </button>

                                        {isStatusOpen && (
                                            <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                                                {['New', 'In Review', 'Underwriting', 'Approved', 'Rejected'].map((status) => (
                                                    <button
                                                        key={status}
                                                        onClick={() => handleStatusChange(status)}
                                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                                                    >
                                                        {status}
                                                        {header?.status === status && <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-6 text-sm">
                                <div className="flex items-center gap-1.5 text-gray-500">
                                    Requested: <span className="font-bold text-gray-900">{header?.requested_amount ? `$${header.requested_amount.toLocaleString()}` : '...'}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-gray-500">
                                    ETA: <span className="font-bold text-gray-900">{header?.eta || '...'}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-gray-500">
                                    Risk: <span className={`px-2 py-0.5 rounded text-xs font-bold ${header?.risk === 'High' ? 'bg-red-100 text-red-700' :
                                        header?.risk === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-green-100 text-green-700'
                                        }`}>{header?.risk || '...'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs Navigation */}
                    <div className="px-8 flex items-center justify-between border-b border-gray-200 mb-6 font-semibold">
                        <div className="flex items-center gap-8">
                            {TABS.map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === tab
                                        ? 'text-gray-900 border-b-2 border-gray-900'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="px-8 pb-8">
                        <div className="w-full">
                            {activeTab === 'Overview' && <Overview />}
                            {activeTab === 'Documents' && <Documents />}
                            {activeTab === 'Credit Memo' && <CreditMemo />}
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Flex sibling Chatbot (No fixed/transform logic) */}
            {isChatOpen && (
                <div className="h-full border-l border-gray-200 transition-all duration-300">
                    <DealChatbot
                        businessSlug={businessSlug || null}
                        isMaximized={isChatMaximized}
                        onToggleMaximize={() => setIsChatMaximized(!isChatMaximized)}
                        onClose={() => setIsChatOpen(false)}
                    />
                </div>
            )}

            {!isChatOpen && (
                <div className="fixed bottom-8 right-8 z-[70]">
                    <button
                        onClick={() => setIsChatOpen(true)}
                        className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-800 transition-all group"
                    >
                        <Bot className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default DealsLayout;

